import React, { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import SearchIcon from 'icons/search.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import useDebounce from 'components/hooks/useDebounce';

import {
  ClearIndicator,
  DropdownIndicator,
  NoOptionsMessage,
  Option,
} from 'components/application/CollectionSelect';
import RowBar from 'components/application/RowBar';
import SearchField from 'components/application/SearchField';
import SegmentedControls from 'components/application/SegmentedControls';
import Toggleable from 'components/application/Toggleable';

import AddButton from 'components/buttons/AddButton';
import { useTabContext } from 'components/contexts/TabContext';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';

export default function BookingsBar({
  bookingSearch,
  onCourseOptionChange,
  onSearchInputChange,
  onSearchReset,
  onSegmentItemClick,
}) {
  const { selectedTabName, tabNames } = useTabContext();
  const { hasBookingGroupEditableAccess } = useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'bookings.BookingsBar',
  });

  const { t: i18nTBookings } = useTranslation('common', {
    keyPrefix: 'bookings',
  });

  const [inputValue, setInputValue] = useState(null);
  const [debouncedInputValue] = useDebounce(inputValue, 250);
  const [isToggled, setIsToggled] = useState(false);

  const handleBlur = () => {
    setIsToggled(false);
  };

  const { data: assignableCourses } = useQuery({
    queryKey: [
      'bookings',
      'assignableCourses',
      { search: debouncedInputValue },
    ],
    queryFn: async () => {
      const response = await axios.get('/bookings/assignable_courses', {
        params: { search: debouncedInputValue },
      });

      return response.data;
    },
    enabled: !!(debouncedInputValue && debouncedInputValue.length > 0),
  });

  const courseOptions =
    assignableCourses ?
      assignableCourses.data.map((course) => ({
        value: course.id,
        label: course.attributes.name,
      }))
    : [];

  return (
    <RowBar
      actions={
        hasBookingGroupEditableAccess ?
          <Toggleable
            isToggled={isToggled}
            primary={<AddButton text={i18nTBookings('addBooking')} />}
            secondary={
              <div className='tw-relative'>
                <SearchIcon
                  className='search-field__toggleable-icon tw-z-[1] tw-ml-[5px] tw-mt-[5px] [&_path]:tw-fill-grey-700'
                  height={24}
                  width={24}
                />
                <div className='tw-w-60 min-w-1200:tw-w-100'>
                  <div className='collection-select collection-select--inline-form collection-select--inline-search collection-select--large-menu'>
                    <div className='form-group'>
                      <Select
                        autoFocus={true}
                        className='collection-select__select-container'
                        classNamePrefix='collection-select'
                        components={{
                          ClearIndicator,
                          DropdownIndicator,
                          NoOptionsMessage,
                          Option,
                        }}
                        isSearchable={true}
                        name='bookingGroupCourseSearch'
                        onBlur={handleBlur}
                        onChange={({ value }) => {
                          onCourseOptionChange({ courseOptionId: value });
                        }}
                        onInputChange={(value) => setInputValue(value)}
                        options={courseOptions}
                        placeholder={i18nT('select.placeholder')}
                        value={inputValue}
                      />
                    </div>
                  </div>
                </div>
              </div>
            }
            setIsToggled={setIsToggled}
          />
        : null
      }
      content={
        <span className='separated-items tw-flex tw-items-center'>
          <SegmentedControls
            activeChoice={selectedTabName}
            choices={tabNames}
            onItemClick={onSegmentItemClick}
          />
          <SearchField
            additionalClasses='tw-w-70'
            name='bookingSearch'
            onChange={onSearchInputChange}
            onReset={onSearchReset}
            placeholder={i18nT('search.placeholder')}
            value={bookingSearch}
          />
        </span>
      }
      modifiers={[
        'border-top-curved',
        'border-bottom-none',
        'flex-align-items-ie-fix',
      ]}
    />
  );
}

BookingsBar.propTypes = {
  bookingSearch: PropTypes.string,
  onCourseOptionChange: PropTypes.func.isRequired,
  onSearchInputChange: PropTypes.func.isRequired,
  onSearchReset: PropTypes.func.isRequired,
  onSegmentItemClick: PropTypes.func.isRequired,
};
